import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../api/client";
import { Avatar } from "../components/Avatar";
import { AvatarPicker } from "../components/AvatarPicker";
import { ConfirmButton } from "../components/ConfirmButton";
import type {
  ApiTokenWithSecret,
  ServiceUserWithTokens,
  TokenRoleCap,
} from "@kandoo/shared";

/** Selector for a token's board-role ceiling. */
function RoleCapSelect({
  value,
  onChange,
}: {
  value: TokenRoleCap | "";
  onChange: (v: TokenRoleCap | "") => void;
}) {
  return (
    <label className="text-sm">
      <span className="text-slate-600">Access</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as TokenRoleCap | "")}
        className="mt-1 block rounded border border-slate-300 bg-[var(--surface)] px-2 py-2 text-sm"
      >
        <option value="">Full (your role)</option>
        <option value="editor">Editor at most</option>
        <option value="viewer">Viewer (read-only)</option>
      </select>
    </label>
  );
}

function capLabel(cap: string | null): string {
  return cap === "viewer" ? "viewer" : cap === "editor" ? "editor" : "full";
}

export function TokensPage() {
  const qc = useQueryClient();
  const { data: tokens, isLoading } = useQuery({
    queryKey: ["tokens"],
    queryFn: () => api.tokens.list(),
  });

  const [name, setName] = useState("");
  const [roleCap, setRoleCap] = useState<TokenRoleCap | "">("");
  const [minted, setMinted] = useState<ApiTokenWithSecret | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      api.tokens.create({ name, roleCap: roleCap || undefined }),
    onSuccess: (token) => {
      setMinted(token);
      setName("");
      qc.invalidateQueries({ queryKey: ["tokens"] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.tokens.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tokens"] }),
  });

  function copyToken() {
    if (!minted) return;
    void navigator.clipboard.writeText(minted.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">API tokens</h1>
      <p className="mb-4 text-sm text-slate-600">
        Mint tokens to manage boards, lists, and cards via the REST API. The
        full secret is shown <strong>only once</strong> at creation time.
      </p>

      {/* Mint form */}
      <div className="mb-6 rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            createMutation.mutate();
          }}
          className="flex items-end gap-3"
        >
          <label className="flex-1 text-sm">
            <span className="text-slate-600">Token name</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CI deploy script"
            />
          </label>
          <RoleCapSelect value={roleCap} onChange={setRoleCap} />
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending ? "Minting…" : "Generate token"}
          </button>
        </form>

        {minted && (
          <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3">
            <p className="mb-2 text-sm font-medium text-amber-800">
              Copy your token now — it won't be shown again:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-[var(--surface)] px-2 py-1 text-sm">
                {minted.token}
              </code>
              <button
                type="button"
                onClick={copyToken}
                className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMinted(null)}
              className="mt-2 text-xs text-slate-500 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Token list */}
      {isLoading ? (
        <p className="text-slate-500">Loading tokens…</p>
      ) : tokens && tokens.length > 0 ? (
        <table className="w-full text-left text-sm">
          <thead className="text-slate-500">
            <tr>
              <th className="py-2">Name</th>
              <th>Prefix</th>
              <th>Access</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="py-2">{t.name}</td>
                <td>
                  <code>{t.prefix}…</code>
                </td>
                <td className="capitalize">{capLabel(t.roleCap)}</td>
                <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                <td>
                  {t.lastUsedAt
                    ? new Date(t.lastUsedAt).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  {t.revoked ? (
                    <span className="text-slate-400">revoked</span>
                  ) : (
                    <span className="text-green-700">active</span>
                  )}
                </td>
                <td>
                  {!t.revoked && (
                    <button
                      type="button"
                      onClick={() => revokeMutation.mutate(t.id)}
                      className="text-red-600 hover:underline"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-slate-500">No tokens yet.</p>
      )}

      <ServiceUsersSection />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Service users: bot identities (e.g. for an MCP server). Only org owners can
 * create them; their tokens act with the creator's board access while
 * comments/activity are attributed to the service user.
 */
function ServiceUsersSection() {
  const qc = useQueryClient();
  const orgsQ = useQuery({ queryKey: ["orgs"], queryFn: () => api.orgs.list() });
  const suQ = useQuery({
    queryKey: ["service-users"],
    queryFn: () => api.serviceUsers.list(),
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["service-users"] });

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.serviceUsers.create({
        username: username.trim(),
        displayName: displayName.trim() || undefined,
      }),
    onSuccess: () => {
      setUsername("");
      setDisplayName("");
      invalidate();
    },
  });

  const ownsOrg = (orgsQ.data ?? []).some((o) => o.role === "owner");
  const serviceUsers = suQ.data ?? [];
  // section is pointless for non-org-owners with no existing service users
  if (!ownsOrg && serviceUsers.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="mb-1 text-xl font-semibold">Service users</h2>
      <p className="mb-4 text-sm text-slate-600">
        Bot identities for integrations (e.g. an MCP server). Their tokens act
        with <strong>your</strong> board access, but comments and activity are
        attributed to the service user. They can't log in.
      </p>

      {ownsOrg && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (username.trim()) create.mutate();
          }}
          className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-[var(--surface)] p-4"
        >
          <label className="flex-1 text-sm">
            <span className="text-slate-600">Username</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. claude"
            />
          </label>
          <label className="flex-1 text-sm">
            <span className="text-slate-600">Display name (optional)</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Claude"
            />
          </label>
          <button
            type="submit"
            disabled={create.isPending || !username.trim()}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Create service user
          </button>
        </form>
      )}

      {serviceUsers.length === 0 ? (
        <p className="text-sm text-slate-500">No service users yet.</p>
      ) : (
        <ul className="space-y-4">
          {serviceUsers.map((su) => (
            <ServiceUserCard key={su.id} su={su} onChanged={invalidate} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ServiceUserCard({
  su,
  onChanged,
}: {
  su: ServiceUserWithTokens;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [tokenName, setTokenName] = useState("");
  const [tokenCap, setTokenCap] = useState<TokenRoleCap | "">("");
  const [minted, setMinted] = useState<ApiTokenWithSecret | null>(null);
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const setAvatar = useMutation({
    mutationFn: (avatar: string | null) =>
      api.serviceUsers.updateAvatar(su.id, avatar),
    onSuccess: () => {
      onChanged();
      // so the bot's new avatar resolves everywhere it's rendered
      qc.invalidateQueries({ queryKey: ["avatars"] });
    },
  });

  const mint = useMutation({
    mutationFn: () =>
      api.serviceUsers.mintToken(su.id, {
        name: tokenName,
        roleCap: tokenCap || undefined,
      }),
    onSuccess: (t) => {
      setMinted(t);
      setTokenName("");
      onChanged();
    },
  });
  const revoke = useMutation({
    mutationFn: (tokenId: string) => api.serviceUsers.revokeToken(su.id, tokenId),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => api.serviceUsers.delete(su.id),
    onSuccess: onChanged,
  });

  function copyToken() {
    if (!minted) return;
    void navigator.clipboard.writeText(minted.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <li className="rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
      <div className="flex items-center gap-3">
        <Avatar
          userId={su.id}
          name={su.displayName || su.username}
          size="md"
          avatar={su.avatar}
        />
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-800">
            {su.displayName || su.username}
          </div>
          <div className="text-xs text-slate-500">@{su.username}</div>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          aria-expanded={pickerOpen}
          className="rounded px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        >
          {pickerOpen ? "Done" : "Change avatar"}
        </button>
        <ConfirmButton
          onConfirm={() => remove.mutate()}
          title="Delete service user"
          className="rounded px-2 py-1 text-sm text-slate-400 hover:bg-red-50 hover:text-red-600"
          confirmClassName="rounded bg-red-600 px-2 py-1 text-sm text-white"
          confirmLabel="Delete? (removes its comments)"
        >
          Delete
        </ConfirmButton>
      </div>

      {pickerOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <AvatarPicker
            userId={su.id}
            name={su.displayName || su.username}
            value={su.avatar}
            disabled={setAvatar.isPending}
            onSelect={(v) => setAvatar.mutate(v)}
          />
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (tokenName.trim()) mint.mutate();
        }}
        className="mt-3 flex gap-2"
      >
        <input
          value={tokenName}
          onChange={(e) => setTokenName(e.target.value)}
          placeholder="New token name (e.g. mcp-server)"
          className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-blue-400"
        />
        <select
          value={tokenCap}
          onChange={(e) => setTokenCap(e.target.value as TokenRoleCap | "")}
          aria-label="Token access"
          className="rounded border border-slate-300 bg-[var(--surface)] px-2 py-1.5 text-sm"
        >
          <option value="">Full (your role)</option>
          <option value="editor">Editor at most</option>
          <option value="viewer">Viewer (read-only)</option>
        </select>
        <button
          type="submit"
          disabled={mint.isPending || !tokenName.trim()}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Mint token
        </button>
      </form>

      {minted && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3">
          <p className="mb-2 text-sm font-medium text-amber-800">
            Copy this token now — it won't be shown again:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-[var(--surface)] px-2 py-1 text-sm">
              {minted.token}
            </code>
            <button
              type="button"
              onClick={copyToken}
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMinted(null)}
            className="mt-2 text-xs text-slate-500 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {su.tokens.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          {su.tokens.map((t) => (
            <li key={t.id} className="py-1.5">
              <div className="flex items-center gap-3">
                <span className="flex-1">{t.name}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs capitalize text-slate-600">
                  {capLabel(t.roleCap)}
                </span>
                <code className="text-xs text-slate-500">{t.prefix}…</code>
                {t.revoked ? (
                  <span className="text-xs text-slate-400">revoked</span>
                ) : (
                  <>
                    <span className="text-xs text-green-700">active</span>
                    <button
                      type="button"
                      onClick={() => revoke.mutate(t.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Revoke
                    </button>
                  </>
                )}
              </div>
              {!t.revoked && <TrustedIdentities tokenId={t.id} />}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
/* -------------------------------------------------------------------------- */

/**
 * Whose @mentions this token acts on.
 *
 * Anyone who shares a board with a service user can @mention it, so a mention is
 * not on its own a reason for the agent to do anything. This is the list of
 * people whose word it may act on; everyone else's mentions still arrive, but
 * arrive marked untrusted, to be reported rather than obeyed.
 *
 * It only vouches for the *author* of a mention — not for the card description,
 * attachments, or other comments that mention points at, which anyone on the
 * board may have written.
 */
function TrustedIdentities({ tokenId }: { tokenId: string }) {
  const qc = useQueryClient();
  const key = ["token-trusted", tokenId];
  const { data: trusted = [] } = useQuery({
    queryKey: key,
    queryFn: () => api.trusted.list(tokenId),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: (username: string) => api.trusted.add(tokenId, username),
    onSuccess: () => {
      setName("");
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.trusted.remove(tokenId, userId),
    onSuccess: invalidate,
  });

  const [name, setName] = useState("");

  return (
    <div className="mt-1.5 pl-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          title="Mentions from these people are treated as requests. Everyone else's arrive marked untrusted."
          className="text-xs text-slate-500"
        >
          Acts on mentions from:
        </span>
        {trusted.length === 0 && (
          <span className="text-xs text-amber-700">
            nobody — every mention it sees is untrusted
          </span>
        )}
        {trusted.map((u) => (
          <span
            key={u.userId}
            className="flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2 pr-1 text-xs text-slate-700"
          >
            @{u.username}
            <button
              type="button"
              onClick={() => remove.mutate(u.userId)}
              aria-label={`Stop trusting ${u.username}`}
              className="rounded-full px-1 text-slate-400 hover:bg-red-100 hover:text-red-600"
            >
              ×
            </button>
          </span>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) add.mutate(name.trim());
          }}
          className="flex items-center gap-1"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="+ username"
            aria-label="Trust another identity"
            className="w-28 rounded border border-slate-300 px-1.5 py-0.5 text-xs outline-none focus:border-blue-400"
          />
        </form>
      </div>
      {add.isError && (
        <p className="mt-1 text-xs text-red-600">
          {(add.error as Error).message}
        </p>
      )}
    </div>
  );
}
